import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_ITEM_SELECTOR =
  'input:not([disabled]), button:not([disabled]), [role="button"][tabindex], label[tabindex]';

interface UseFilterListKeyboardNavigationOptions {
  listRef: React.RefObject<HTMLElement | null>;
  /** Selector for focusable items within the list */
  itemSelector?: string;
  /** Whether to loop at edges (default: true) */
  loop?: boolean;
  /** Disable navigation */
  disabled?: boolean;
  /** Called on Enter/Space instead of click() - receives the index and element of the focused item */
  onActivate?: (index: number, element: HTMLElement) => void;
}

interface UseFilterListKeyboardNavigationReturn {
  handleKeyDown: (e: React.KeyboardEvent) => void;
  focusedIndex: number;
  resetFocusIndex: (index?: number) => void;
  collectItems: () => void;
}

export function useFilterListKeyboardNavigation(
  options: UseFilterListKeyboardNavigationOptions,
): UseFilterListKeyboardNavigationReturn {
  const { listRef, itemSelector = DEFAULT_ITEM_SELECTOR, loop = true, disabled = false, onActivate } = options;

  const itemsRef = useRef<HTMLElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const collectItems = useCallback(() => {
    if (!listRef.current) {
      itemsRef.current = [];
      return;
    }
    itemsRef.current = Array.from(listRef.current.querySelectorAll<HTMLElement>(itemSelector));
  }, [listRef, itemSelector]);

  // Apply roving tabindex: focused item gets tabIndex=0, rest get -1
  const applyTabIndices = useCallback((activeIndex: number) => {
    const items = itemsRef.current;
    items.forEach((item, i) => {
      item.tabIndex = i === activeIndex ? 0 : -1;
    });
  }, []);

  // Re-collect items and enforce roving tabindex after every render.
  // useLayoutEffect runs synchronously after React commits DOM changes
  // but before the browser paints, so tabIndex values are correct
  // before any user interaction (Tab, arrow keys, etc.).
  // This is necessary because React re-renders reset tabIndex on components
  // that explicitly set it as a prop (e.g. text-swatch, color-swatch).
  useLayoutEffect(() => {
    if (disabled) {
      return;
    }
    collectItems();
    applyTabIndices(focusedIndex);
  });

  const getNextIndex = useCallback(
    (currentIndex: number, delta: number): number => {
      const total = itemsRef.current.length;
      if (total === 0) {
        return currentIndex;
      }

      const nextIndex = currentIndex + delta;
      if (loop) {
        if (nextIndex < 0) {
          return total - 1;
        }
        if (nextIndex >= total) {
          return 0;
        }
        return nextIndex;
      }
      return Math.max(0, Math.min(nextIndex, total - 1));
    },
    [loop],
  );

  // Detect the current focused index from document.activeElement
  const detectFocusedIndex = useCallback((): number => {
    const active = document.activeElement as HTMLElement;
    if (!active) {
      return focusedIndex;
    }

    const idx = itemsRef.current.indexOf(active);
    return idx >= 0 ? idx : focusedIndex;
  }, [focusedIndex]);

  const moveFocus = useCallback(
    (index: number) => {
      const item = itemsRef.current[index];
      if (item) {
        applyTabIndices(index);
        item.focus();
      }
    },
    [applyTabIndices],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || itemsRef.current.length === 0) {
        return;
      }

      const currentIndex = detectFocusedIndex();
      let newIndex = currentIndex;
      let handled = false;

      switch (e.key) {
        case 'ArrowDown': {
          newIndex = getNextIndex(currentIndex, 1);
          handled = true;
          break;
        }
        case 'ArrowUp': {
          newIndex = getNextIndex(currentIndex, -1);
          handled = true;
          break;
        }
        case 'Home': {
          newIndex = 0;
          handled = true;
          break;
        }
        case 'End': {
          newIndex = itemsRef.current.length - 1;
          handled = true;
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          e.stopPropagation();
          if (onActivate) {
            onActivate(currentIndex, itemsRef.current[currentIndex]);
          } else {
            itemsRef.current[currentIndex]?.click();
          }
          return;
        }
        default:
          return;
      }

      if (handled) {
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex(newIndex);
        moveFocus(newIndex);
      }
    },
    [disabled, detectFocusedIndex, getNextIndex, moveFocus, onActivate],
  );

  const resetFocusIndex = useCallback(
    (index = 0) => {
      collectItems();
      const clampedIndex = Math.max(0, Math.min(index, itemsRef.current.length - 1));
      setFocusedIndex(clampedIndex);
      applyTabIndices(clampedIndex);
    },
    [collectItems, applyTabIndices],
  );

  return {
    handleKeyDown,
    focusedIndex,
    resetFocusIndex,
    collectItems,
  };
}

export type { UseFilterListKeyboardNavigationOptions, UseFilterListKeyboardNavigationReturn };
